import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCurrentLanguage, getLanguageFromPath, validateLanguageCode } from '../utils/languageUtils';
import i18n from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const location = useLocation();

  // Initialize language from URL, localStorage (preferred_locale or selectedLanguage), or browser
  useEffect(() => {
    const detectedLang = getCurrentLanguage(location.pathname);
    const validLang = validateLanguageCode(detectedLang);
    setCurrentLanguage(validLang);
    
    // Update browser language for the translation pipeline
    if (typeof window !== 'undefined') {
      document.documentElement.lang = validLang;
    }

    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(validLang);
    }
  }, [location.pathname]);

  // Listen for localStorage changes (preferred_locale from livelovespain.com or selectedLanguage)
  useEffect(() => {
    const checkLocalStorage = () => {
      if (typeof window === 'undefined') return;
      
      // Check preferred_locale first (from livelovespain.com)
      const preferredLocale = localStorage.getItem('preferred_locale');
      const selectedLanguage = localStorage.getItem('selectedLanguage');
      
      let langToUse = null;
      if (preferredLocale && validateLanguageCode(preferredLocale) !== 'en') {
        langToUse = validateLanguageCode(preferredLocale);
      } else if (selectedLanguage && validateLanguageCode(selectedLanguage) !== 'en') {
        langToUse = validateLanguageCode(selectedLanguage);
      }
      
      if (langToUse && langToUse !== currentLanguage) {
        setCurrentLanguage(langToUse);
        if (i18n && i18n.changeLanguage) {
          i18n.changeLanguage(langToUse);
        }
        if (typeof window !== 'undefined') {
          document.documentElement.lang = langToUse;
        }
      }
    };

    // Check on mount
    checkLocalStorage();

    // Listen for storage events (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'preferred_locale' || e.key === 'selectedLanguage') {
        checkLocalStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case localStorage was updated in same tab
    const interval = setInterval(checkLocalStorage, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLanguage]);

  // Note: URL normalization is now handled by LanguageSelector navigation
  // This effect was causing conflicts with explicit navigation, so it's disabled
  // The LanguageSelector's goToLang function handles URL updates correctly

  // Listen for language changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedLanguage' && e.newValue) {
        setCurrentLanguage(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const changeLanguage = (langCode) => {
    const validLang = validateLanguageCode(langCode);
    setCurrentLanguage(validLang);
    
    // Update both localStorage keys for compatibility
    localStorage.setItem('selectedLanguage', validLang);
    localStorage.setItem('preferred_locale', validLang);
    
    // Update browser language
    if (typeof window !== 'undefined') {
      document.documentElement.lang = validLang;
    }

    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(validLang);
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
